import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import {
  getAlertsApi,
  acknowledgeAlertApi,
  enableAccelerationApi,
  dismissAlertApi,
} from 'api/alertApi';
import { getCustomerProfilesApi } from 'api/adminApi';
import DataTable from 'components/DataTable';
import Card from 'components/card/Card';
import moment from 'moment';
import { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { roasCovenantMonthFromApi } from '../Operations/roasCovenantMonth';

const STATUS_COLORS = {
  pending: 'orange',
  acknowledged: 'blue',
  acceleration_enabled: 'green',
  dismissed: 'gray',
};

const STATUS_LABELS = {
  pending: 'Pending',
  acknowledged: 'Acknowledged',
  acceleration_enabled: 'Acceleration Enabled',
  dismissed: 'Dismissed',
};

const ALERT_TYPE_LABELS = {
  roas_breach: 'ROAS Breach',
  min_roas_growth: 'Min ROAS Growth',
};

const ALERT_TYPE_COLORS = {
  roas_breach: 'red',
  min_roas_growth: 'purple',
};

const Alerts = () => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [customerNames, setCustomerNames] = useState({});
  const [customersReady, setCustomersReady] = useState(false);

  const toast = useToast();
  const columnHelper = createColumnHelper();

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await getCustomerProfilesApi();
        const map = {};
        (Array.isArray(customers) ? customers : []).forEach((c) => {
          map[c.id] = c.companyName;
        });
        setCustomerNames(map);
      } catch (_) {}
      setCustomersReady(true);
    };
    fetchCustomers();
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await getAlertsApi(params);
      setAlerts(data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load alerts',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleAction = async (alertId, action, actionLabel) => {
    setActionLoading(alertId);
    try {
      await action({ alertId });
      toast({
        title: 'Success',
        description: `Alert ${actionLabel} successfully`,
        status: 'success',
        duration: 3000,
      });
      fetchAlerts();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${actionLabel.toLowerCase()} alert`,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const filterAlerts = () => {
    return alerts.filter((alert) => customerNames[alert.customer_id]);
  };

  const columns = [
    columnHelper.accessor('created_at', {
      header: 'Date',
      cell: (info) => (
        <Text fontSize="sm">
          {info.getValue()
            ? moment(info.getValue()).format('MMM DD, YYYY HH:mm')
            : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('customer_id', {
      header: 'Customer',
      cell: (info) => {
        const cid = info.getValue();
        const name = customerNames[cid];
        return (
          <Tooltip label={cid} fontSize="xs">
            <Text fontSize="sm" cursor="default">
              {name || cid?.substring(0, 8) + '...'}
            </Text>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('installment_id', {
      header: 'Installment',
      cell: (info) => {
        const installmentId = info.getValue();
        const customerId = info.row.original.customer_id;
        return (
          <Tooltip label={installmentId} fontSize="xs">
            <ChakraLink
              as={RouterLink}
              to={`/operations/${customerId}/installment/${installmentId}`}
              fontSize="sm"
              fontFamily="mono"
              color="blue.500"
              _hover={{ textDecoration: 'underline' }}
            >
              {installmentId?.substring(0, 12)}...
            </ChakraLink>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('alert_type', {
      header: 'Type',
      cell: (info) => {
        const alertType = info.getValue();
        return (
          <Tag
            size="sm"
            colorScheme={ALERT_TYPE_COLORS[alertType] || 'gray'}
            variant="subtle"
          >
            {ALERT_TYPE_LABELS[alertType] || alertType}
          </Tag>
        );
      },
    }),
    columnHelper.accessor('period', {
      header: 'Month',
      cell: (info) => {
        const mob = roasCovenantMonthFromApi(info.getValue());
        return <Text fontSize="sm">{mob === '' ? '-' : mob}</Text>;
      },
    }),
    columnHelper.accessor('actual_roas_pct', {
      header: 'Actual ROAS',
      cell: (info) => {
        const val = info.getValue();
        return (
          <Text fontSize="sm" color="red.500" fontWeight="semibold">
            {val != null ? `${(val * 100).toFixed(1)}%` : '-'}
          </Text>
        );
      },
    }),
    columnHelper.accessor('threshold_roas_pct', {
      header: 'Threshold',
      cell: (info) => {
        const val = info.getValue();
        return (
          <Text fontSize="sm">
            {val != null ? `${(val * 100).toFixed(1)}%` : '-'}
          </Text>
        );
      },
    }),
    columnHelper.accessor('acceleration_pct', {
      header: 'Acceleration %',
      cell: (info) => {
        const val = info.getValue();
        return val ? (
          <Text fontSize="sm">{val}%</Text>
        ) : (
          <Text fontSize="sm" color="gray.400">
            -
          </Text>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const status = info.getValue();
        return (
          <Badge colorScheme={STATUS_COLORS[status] || 'gray'} fontSize="xs">
            {STATUS_LABELS[status] || status}
          </Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const row = info.row.original;
        const isLoading = actionLoading === row.id;

        if (row.status !== 'pending') {
          return null;
        }

        return (
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              isLoading={isLoading}
            >
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem
                fontSize="sm"
                onClick={() =>
                  handleAction(row.id, acknowledgeAlertApi, 'acknowledged')
                }
              >
                Acknowledge
              </MenuItem>
              <MenuItem
                fontSize="sm"
                color="green.600"
                fontWeight="semibold"
                onClick={() =>
                  handleAction(
                    row.id,
                    enableAccelerationApi,
                    'acceleration enabled'
                  )
                }
              >
                Enable Acceleration
              </MenuItem>
              <MenuItem
                fontSize="sm"
                color="gray.500"
                onClick={() =>
                  handleAction(row.id, dismissAlertApi, 'dismissed')
                }
              >
                Dismiss
              </MenuItem>
            </MenuList>
          </Menu>
        );
      },
    }),
  ];

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="lg">Alerts</Heading>
        <HStack spacing={3}>
          <Select
            size="sm"
            w="180px"
            placeholder="All statuses"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="acceleration_enabled">Acceleration Enabled</option>
            <option value="dismissed">Dismissed</option>
          </Select>
          <Button size="sm" onClick={fetchAlerts} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      <Card>
        {(loading || !customersReady) && alerts.length === 0 ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" />
          </Flex>
        ) : filterAlerts().length === 0 ? (
          <Flex justify="center" align="center" minH="200px">
            <Text color="gray.500">No alerts found</Text>
          </Flex>
        ) : (
          <Box mx={-5}>
            <DataTable
              data={filterAlerts()}
              columns={columns}
              hasPagination={true}
              defaultSorting={[{ id: 'created_at', desc: true }]}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default Alerts;
